<template>
  <div class="brand-step">
    <!-- Blocked: business profile still under Twilio review -->
    <collapsible-section v-if="state === 'blocked'" :collapsible="false" title="Waiting on business profile approval" icon="tim-icons icon-time-alarm">
      <div class="brand-step__status">
        <span class="brand-step__pulse"></span>
        <div>
          <p class="brand-step__lead">Twilio is reviewing your business profile and its messaging authorization. Both must approve before the brand step unlocks; this usually takes 1 to 3 business days.</p>
          <p class="brand-step__hint mb-0">We check for updates every few minutes and this page refreshes automatically. You can close it and come back any time.</p>
        </div>
      </div>
    </collapsible-section>

    <!-- Ready: profile approved, brand can be submitted -->
    <collapsible-section v-else-if="state === 'ready'" :collapsible="false" title="Ready to submit" icon="tim-icons icon-badge">
      <p class="brand-step__lead">Your business profile is approved. The next step registers your brand with the A2P 10DLC registry, which is how US carriers verify who is sending texts.</p>
      <ul class="brand-step__list">
        <li>Submission uses the business details you already provided, nothing new to fill in</li>
        <li>Review usually takes 1 to 3 business days</li>
        <li>If anything is rejected you will see the exact feedback here and can fix and resubmit</li>
      </ul>
      <info-hint icon="icon-coins" variant="neutral">
        Submitting charges a one-time $20 activation fee for carrier registration. If the registry rejects a submission, fixing and resubmitting it is free.
      </info-hint>
    </collapsible-section>

    <!-- In review -->
    <collapsible-section v-else-if="state === 'review'" :collapsible="false" title="Brand under review" icon="tim-icons icon-badge">
      <div class="brand-step__status">
        <span class="brand-step__pulse"></span>
        <div>
          <p class="brand-step__lead">Your brand registration is with the carriers. This usually takes 1 to 3 business days.</p>
          <p class="brand-step__hint mb-0">We check for updates every few minutes and this page refreshes automatically.</p>
        </div>
      </div>
    </collapsible-section>

    <!-- Rejected: verbatim carrier feedback + resubmit path -->
    <collapsible-section v-else-if="state === 'rejected'" :collapsible="false" title="Brand registration rejected" icon="tim-icons icon-alert-circle-exc">
      <p class="brand-step__lead">The registry rejected your brand with the following feedback:</p>
      <div class="brand-step__feedback">{{ feedback }}</div>
      <p class="brand-step__hint">Most rejections are fixed by correcting the business details to exactly match IRS records, then resubmitting.</p>
      <base-button type="danger" size="sm" simple @click="$emit('edit-profile')">
        Fix business details
      </base-button>
    </collapsible-section>

    <!-- Complete -->
    <collapsible-section v-else :collapsible="false" title="Brand approved" icon="tim-icons icon-check-2">
      <p class="brand-step__lead mb-1">Your brand is registered with the A2P 10DLC registry.</p>
      <p class="brand-step__hint mb-0" v-if="settings && settings.a2p_brand_sid">Registration id: {{ settings.a2p_brand_sid }}</p>
    </collapsible-section>
  </div>
</template>

<script>
import CollapsibleSection from '@/pages/Bookings/BookinSiteSettings/components/shared/CollapsibleSection';
import InfoHint from '@/components/InfoHint.vue';

const STATE_BY_STATUS = {
  profile_pending: 'blocked',
  profile_review: 'blocked',
  brand_pending: 'ready',
  brand_review: 'review',
  brand_rejected: 'rejected'
};

export default {
  name: 'SMSOnboardingStepBrand',
  components: {
    CollapsibleSection,
    InfoHint
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    settings: {
      type: Object,
      default: null
    }
  },
  computed: {
    state() {
      const status = this.settings?.registration_status || 'not_started';
      // Statuses past the brand stage mean this step is complete.
      return STATE_BY_STATUS[status] || 'complete';
    },
    feedback() {
      return this.settings?.brand_review_feedback || 'No detail was provided.';
    }
  }
};
</script>

<style lang="scss" scoped>
.brand-step {
  &__lead {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.875rem;
    line-height: 1.55;
    margin-bottom: 0.75rem;
  }

  &__hint {
    color: rgba(255, 255, 255, 0.65);
    font-size: 0.75rem;
    line-height: 1.5;
  }

  &__list {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.875rem;
    line-height: 1.6;
    padding-left: 1.25rem;
    margin-bottom: 1rem;
  }

  &__status {
    display: flex;
    align-items: flex-start;
    gap: 14px;
  }

  &__pulse {
    flex-shrink: 0;
    width: 10px;
    height: 10px;
    margin-top: 6px;
    border-radius: 50%;
    background: #e14eca;
    box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.18);
    animation: brand-step-pulse 1.8s ease-in-out infinite;
  }

  &__feedback {
    white-space: pre-wrap;
    font-size: 0.8125rem;
    line-height: 1.55;
    color: #fd77a4;
    background: rgba(253, 93, 147, 0.08);
    border-left: 3px solid #fd5d93;
    border-radius: 0 0.375rem 0.375rem 0;
    padding: 12px 16px;
    margin-bottom: 0.75rem;
  }
}

@keyframes brand-step-pulse {
  0%, 100% { box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.18); }
  50% { box-shadow: 0 0 0 7px rgba(225, 78, 202, 0.08); }
}
</style>

<style>
.white-content .brand-step__lead { color: rgba(0, 0, 0, 0.8); }
.white-content .brand-step__hint { color: rgba(0, 0, 0, 0.6); }
.white-content .brand-step__list { color: rgba(0, 0, 0, 0.8); }
.white-content .brand-step__feedback {
  color: #c81e58;
  background: rgba(253, 93, 147, 0.08);
}
</style>
